import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import tokenPriceStore from '../../services/tokenPriceStore';

const RebalanceManager = ({ 
  contract, 
  governanceContract,
  setError, 
  setSuccess, 
  setLoading, 
  loading,
  onRedirectToGovernance,
  isOwner = false,
  isOperator = false,
  hasBoardSeats = false,
  boardSeats = 0
}) => {
  // Core state
  const [slippageTolerance, setSlippageTolerance] = useState(100); // Default 1%
  const [tokens, setTokens] = useState([]);
  const [rebalanceData, setRebalanceData] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [executingStep, setExecutingStep] = useState('');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMultiProposalConfirm, setShowMultiProposalConfirm] = useState(false);
  
  // Debug state
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugData, setDebugData] = useState(null);
  
  // Governance state
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalAction, setProposalAction] = useState({
    type: '',
    description: '',
    params: {}
  });

  // Transaction tracking
  const [lastTxHash, setLastTxHash] = useState('');
  const [txStatus, setTxStatus] = useState('');

  // Load tokens and their details
  useEffect(() => {
    if (contract) {
      loadTokenData();
    }
  }, [contract]);

  // FIXED: Check if user has permission (owners and operators have permission)
  const hasPermission = () => {
    const hasOwnerPermission = !!isOwner;
    const hasOperatorPermission = !!isOperator;
    
    // Debug log
    console.log("Permission check (strict):", {
      isOwner: hasOwnerPermission,
      isOperator: hasOperatorPermission,
      hasBoardSeats: !!hasBoardSeats,
      boardSeats: Number(boardSeats),
      hasPermission: hasOwnerPermission || hasOperatorPermission
    });
    
    // FIXED: Both contract owner and operators can directly execute transactions
    return hasOwnerPermission || hasOperatorPermission;
  };
  
  // Common handler for all actions that might need governance approval
  const handleGovernanceAction = (actionType, description, params, directAction) => {
    // Always check permission first
    if (!hasPermission()) {
      console.log(`No permission for action: ${description}. Creating governance proposal.`);
      handleUnauthorizedAction(actionType, description, params);
      return;
    }
    
    // If we have permission, execute the direct action
    console.log(`Has permission for action: ${description}. Executing directly.`);
    directAction();
  };
  
  // Function to handle unauthorized actions by redirecting to governance
  const handleUnauthorizedAction = (actionType, description, params = {}) => {
    console.log("Creating governance proposal for:", description, params);
    
    setProposalAction({
      type: actionType,
      description,
      params
    });
    setShowProposalModal(true);
  };

  // Create sell token call data - UPDATED to match TokenManager.jsx
  const createSellTokenCallData = (tokenAddress, amount) => {
    try {
      // Create an ethers Interface with the function definition
      const iface = new ethers.utils.Interface([
        "function sellToken(address tokenAddress, uint256 amount)"
      ]);
      
      // Encode the function call with parameters
      const callData = iface.encodeFunctionData("sellToken", [tokenAddress, amount]);
      console.log(`Generated call data for sellToken: ${callData}`);
      return callData;
    } catch (error) {
      console.error("Error creating sell token call data:", error);
      throw error;
    }
  };

  // Create buy token call data
  const createBuyTokenCallData = (tokenAddress, ethAmount) => {
    try {
      const iface = new ethers.utils.Interface([
        "function buyToken(address tokenAddress, uint256 ethAmount)"
      ]);
      
      const callData = iface.encodeFunctionData("buyToken", [tokenAddress, ethAmount]);
      console.log(`Generated call data for buyToken: ${callData}`);
      return callData;
    } catch (error) {
      console.error("Error creating buy token call data:", error);
      throw error;
    }
  };

  // Create rebalance portfolio call data
  const createRebalancePortfolioCallData = () => {
    try {
      const iface = new ethers.utils.Interface([
        "function rebalancePortfolio()"
      ]);
      
      const callData = iface.encodeFunctionData("rebalancePortfolio", []);
      console.log(`Generated call data for rebalancePortfolio: ${callData}`);
      return callData;
    } catch (error) {
      console.error("Error creating rebalance portfolio call data:", error);
      throw error;
    }
  };

  const loadTokenData = async () => {
    try {
      setLoading(true);
      
      // Get token count
      const tokenCount = await contract.getTokenCount();
      console.log(`Found ${tokenCount} tokens in fund`);
      
      const tokenList = [];
      const tokenAddresses = [];
      
      // Load info for each token
      for (let i = 0; i < tokenCount; i++) {
        const tokenInfo = await contract.getTokenInfo(i);
        const tokenAddress = tokenInfo.tokenAddress;
        const allocation = parseInt(tokenInfo.allocation.toString());
        
        // Create token contract instance
        const tokenContract = new ethers.Contract(
          tokenAddress,
          ["function balanceOf(address) view returns (uint256)", "function decimals() view returns (uint8)", "function symbol() view returns (string)"],
          contract.provider
        );
        
        // Get token balance and metadata
        const balance = await tokenContract.balanceOf(contract.address);
        let decimals = 18; // Default to 18 decimals
        let symbol = `Token ${i+1}`; // Default symbol
        
        try {
          decimals = await tokenContract.decimals();
          symbol = await tokenContract.symbol();
        } catch (error) {
          console.warn(`Failed to get token metadata for ${tokenAddress}`, error);
        }
        
        tokenList.push({
          address: tokenAddress,
          allocation,
          balance,
          decimals,
          symbol,
          formattedBalance: ethers.utils.formatUnits(balance, decimals)
        });
        
        tokenAddresses.push(tokenAddress);
      }
      
      // Get prices for tokens
      const provider = contract.provider;
      const prices = tokenPriceStore.getPrices(tokenAddresses);

      // Add prices to token list
      const tokensWithPrices = tokenList.map(token => {
        const price = prices[token.address.toLowerCase()] || 0;
        const valueUSD = parseFloat(token.formattedBalance) * price;
        return {
          ...token,
          price,
          valueUSD
        };
      });
      
      setTokens(tokensWithPrices);
      console.log("Loaded tokens with prices:", tokensWithPrices);
    } catch (error) {
      console.error("Error loading token data:", error);
      setError("Failed to load token data");
    } finally {
      setLoading(false);
    }
  };

  const calculateRebalance = async () => {
    try {
      setCalculating(true);
      
      // Calculate total value of portfolio
      const totalValueUSD = tokens.reduce((sum, token) => sum + token.valueUSD, 0);
      
      if (totalValueUSD === 0) {
        setError("Portfolio has no value");
        return;
      }
      
      // Calculate current allocations
      const tokensWithCurrentAllocation = tokens.map(token => {
        const currentAllocation = Math.round((token.valueUSD / totalValueUSD) * 10000);
        const targetAllocation = token.allocation;
        const diffBasisPoints = currentAllocation - targetAllocation;
        
        return {
          ...token,
          currentAllocation,
          targetAllocation,
          diffBasisPoints,
          // Positive means overweight (need to sell), negative means underweight (need to buy)
          status: diffBasisPoints > 50 ? 'overweight' : diffBasisPoints < -50 ? 'underweight' : 'balanced'
        };
      });
      
      // Identify tokens to sell (overweight) and buy (underweight)
      const tokensToSell = tokensWithCurrentAllocation.filter(t => t.status === 'overweight');
      const tokensToBuy = tokensWithCurrentAllocation.filter(t => t.status === 'underweight');
      
      // For overweight tokens, calculate exactly how much to sell
      const sellInstructions = tokensToSell.map(token => {
        const excessAllocationPoints = token.diffBasisPoints;
        const excessValue = (excessAllocationPoints / 10000) * totalValueUSD;
        
        // If price is 0, we can't calculate token amount
        if (token.price <= 0) {
          return {
            ...token,
            amountToSell: ethers.BigNumber.from(0),
            formattedAmountToSell: '0',
            excessValue: 0
          };
        }
        
        // Calculate tokens to sell
        const tokensToSell = excessValue / token.price;
        const amountToSell = ethers.utils.parseUnits(
          tokensToSell.toFixed(token.decimals > 8 ? 8 : token.decimals),
          token.decimals
        );
        
        return {
          ...token,
          amountToSell,
          formattedAmountToSell: ethers.utils.formatUnits(amountToSell, token.decimals),
          excessValue
        };
      });
      
      // For underweight tokens, calculate how many tokens to buy based on USD value
      const buyInstructions = tokensToBuy.map(token => {
        const deficitAllocationPoints = Math.abs(token.diffBasisPoints);
        const deficitValue = (deficitAllocationPoints / 10000) * totalValueUSD;
        
        // Calculate ETH to use (for contract call)
        const ethToUse = ethers.utils.parseEther(deficitValue.toFixed(18).toString());
        const formattedEthToUse = deficitValue.toFixed(4);
        
        // If price is 0, we can't calculate token amount
        if (token.price <= 0) {
          return {
            ...token,
            tokensToBuy: 0,
            formattedAmountToBuy: '0',
            deficitValue,
            ethToUse,
            formattedEthToUse
          };
        }
        
        // Calculate tokens to buy based on USD value / token price
        const tokensToBuy = deficitValue / token.price;
        
        // Format with appropriate precision and store both the precise and formatted values
        // Ensure we're using a number of decimal places appropriate for the token
        const formattedAmountToBuy = parseFloat(tokensToBuy.toFixed(
          token.decimals > 8 ? 8 : token.decimals
        )).toString();
        
        console.log(`Calculating buy for ${token.symbol}: ${deficitValue} / ${token.price} = ${tokensToBuy} tokens, formatted: ${formattedAmountToBuy}`);
        
        return {
          ...token,
          tokensToBuy,
          formattedAmountToBuy,
          deficitValue,
          ethToUse,
          formattedEthToUse
        };
      });
      
      // Summary info
      const totalToSell = sellInstructions.reduce((sum, instr) => sum + instr.excessValue, 0);
      const totalToBuy = buyInstructions.reduce((sum, instr) => sum + instr.deficitValue, 0);
      
      // Final rebalance data
      const rebalanceInstructions = {
        currentPortfolio: tokensWithCurrentAllocation,
        tokensToSell: sellInstructions,
        tokensToBuy: buyInstructions,
        summary: {
          totalValueUSD,
          totalToSell,
          totalToBuy,
          difference: totalToSell - totalToBuy
        }
      };
      
      setRebalanceData(rebalanceInstructions);
      console.log("Rebalance instructions:", rebalanceInstructions);
      
    } catch (error) {
      console.error("Error calculating rebalance:", error);
      setError("Failed to calculate rebalance");
    } finally {
      setCalculating(false);
    }
  };

  // Create sell token proposal - UPDATED based on TokenManager approach
  const createSellTokenProposal = async (tokenInfo) => {
    if (!governanceContract) {
      throw new Error("Governance contract not available");
    }
    
    console.log(`Creating proposal to sell ${tokenInfo.formattedAmountToSell} ${tokenInfo.symbol}`);
    console.log("Token info for sell proposal:", {
      address: tokenInfo.address,
      amount: tokenInfo.amountToSell.toString(),
      formattedAmount: tokenInfo.formattedAmountToSell,
      symbol: tokenInfo.symbol
    });
    
    try {
      // Get encoded call data for selling token
      const callData = createSellTokenCallData(
        tokenInfo.address,
        tokenInfo.amountToSell
      );
      
      // Create a governance proposal
      const description = `Rebalance: Sell ${tokenInfo.formattedAmountToSell} ${tokenInfo.symbol}`;
      const tx = await governanceContract.createProposal(
        4, // ProposalType.SellToken
        description,
        callData
      );
      
      setLastTxHash(tx.hash);
      console.log(`Proposal creation transaction sent: ${tx.hash}`);
      
      return tx;
    } catch (error) {
      console.error("Detailed error creating sell proposal:", {
        message: error.message,
        code: error.code,
        data: error.data,
        tokenInfo: {
          symbol: tokenInfo.symbol,
          address: tokenInfo.address
        }
      });
      throw error;
    }
  };

  // Create buy token proposal
  const createBuyTokenProposal = async (tokenInfo) => {
    if (!governanceContract) {
      throw new Error("Governance contract not available");
    }
    
    console.log(`Creating proposal to buy ${tokenInfo.symbol} using ${tokenInfo.formattedEthToUse} ETH`);
    
    // Get encoded call data for buying token
    const callData = createBuyTokenCallData(
      tokenInfo.address,
      tokenInfo.ethToUse
    );
    
    // Create a governance proposal
    const description = `Rebalance: Buy ${tokenInfo.formattedAmountToBuy} ${tokenInfo.symbol}`;
    const tx = await governanceContract.createProposal(
      3, // ProposalType.BuyToken
      description,
      callData
    );
    
    setLastTxHash(tx.hash);
    console.log(`Proposal creation transaction sent: ${tx.hash}`);
    
    return tx;
  };

  // FIXED: Added a new function to execute rebalance portfolio directly
  const executeDirectRebalance = async () => {
    if (!contract) {
      setError("Contract not available");
      return;
    }
    
    setLoading(true);
    setCompletedSteps([]);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    
    try {
      setExecutingStep(`Initiating portfolio rebalance operation...`);
      
      // Call the contract's rebalancePortfolio function
      // No need to pass slippage - the contract will use its default or the owner's preference
      const tx = await contract.rebalancePortfolio({ gasLimit: 2000000 });
      
      setLastTxHash(tx.hash);
      setTxStatus('Mining');
      
      await tx.wait();
      setTxStatus('Confirmed');
      
      setCompletedSteps(prev => [...prev, `Portfolio rebalanced successfully`]);
      setSuccess("Portfolio rebalanced successfully");
      
      // Refresh token data after rebalance
      await loadTokenData();
      
    } catch (error) {
      console.error("Error rebalancing portfolio:", error);
      setError(`Failed to rebalance portfolio: ${error.message}`);
      setTxStatus('Failed');
    } finally {
      setLoading(false);
      setExecutingStep('');
    }
  };

  const proceedWithMultipleProposals = async () => {
    setShowMultiProposalConfirm(false);
    
    // Create a series of governance proposals
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    
    try {
      const { tokensToSell, tokensToBuy } = rebalanceData;
      let createdProposals = [];
      
      // Create proposals for selling overweight tokens
      for (const tokenInfo of tokensToSell) {
        if (tokenInfo.amountToSell.gt(0)) {
          setExecutingStep(`Creating proposal to sell ${tokenInfo.formattedAmountToSell} ${tokenInfo.symbol}...`);
          
          try {
            const tx = await createSellTokenProposal(tokenInfo);
            setTxStatus('Mining');
            
            await tx.wait();
            setTxStatus('Confirmed');
            
            createdProposals.push(`Sell ${tokenInfo.formattedAmountToSell} ${tokenInfo.symbol}`);
            setCompletedSteps(prev => [...prev, `Created proposal to sell ${tokenInfo.formattedAmountToSell} ${tokenInfo.symbol}`]);
            
            // Wait a moment between proposal creations to avoid nonce issues
            await new Promise(resolve => setTimeout(resolve, 2000));
          } catch (error) {
            console.error(`Error creating proposal to sell ${tokenInfo.symbol}:`, error);
            setError(`Failed to create proposal to sell ${tokenInfo.symbol}: ${error.message}`);
            setTxStatus('Failed');
            break;
          }
        }
      }
      
      // Create proposals for buying underweight tokens
      for (const tokenInfo of tokensToBuy) {
        if (tokenInfo.ethToUse.gt(0)) {
          setExecutingStep(`Creating proposal to buy ${tokenInfo.formattedAmountToBuy} ${tokenInfo.symbol}...`);
          
          try {
            const tx = await createBuyTokenProposal(tokenInfo);
            setTxStatus('Mining');
            
            await tx.wait();
            setTxStatus('Confirmed');
            
            createdProposals.push(`Buy ${tokenInfo.formattedAmountToBuy} ${tokenInfo.symbol}`);
            setCompletedSteps(prev => [...prev, `Created proposal to buy ${tokenInfo.formattedAmountToBuy} ${tokenInfo.symbol}`]);
            
            // Wait a moment between proposal creations to avoid nonce issues
            await new Promise(resolve => setTimeout(resolve, 2000));
          } catch (error) {
            console.error(`Error creating proposal to buy ${tokenInfo.symbol}:`, error);
            setError(`Failed to create proposal to buy ${tokenInfo.symbol}: ${error.message}`);
            setTxStatus('Failed');
            break;
          }
        }
      }
      
      if (createdProposals.length > 0) {
        setSuccess(`Successfully created ${createdProposals.length} rebalance proposals. They will need to be voted on and executed individually.`);
      } else {
        setSuccess("No rebalance proposals were created.");
      }
      
    } catch (error) {
      console.error("Error creating rebalance proposals:", error);
      setError(`Failed to create rebalance proposals: ${error.message}`);
      setTxStatus('Failed');
    } finally {
      setLoading(false);
      setExecutingStep('');
    }
  };

  // FIXED: Completely revamped executeRebalance to support direct rebalancing
  const executeRebalance = async () => {
    if (!rebalanceData) {
      setError("Rebalance data not available");
      return;
    }
    
    setShowConfirmModal(false);
    
    // If user has permission to execute directly
    if (hasPermission()) {
      // APPROACH 1: Use the contract's rebalancePortfolio function directly
      if (contract.rebalancePortfolio) {
        try {
          setLoading(true);
          setCompletedSteps([]);
          setError('');
          setSuccess('');
          setTxStatus('Pending');
          
          setExecutingStep(`Initiating portfolio rebalance operation...`);
          
          // Call the contract's rebalancePortfolio function
          const tx = await contract.rebalancePortfolio({ gasLimit: 2000000 });
          
          setLastTxHash(tx.hash);
          setTxStatus('Mining');
          
          await tx.wait();
          setTxStatus('Confirmed');
          
          setCompletedSteps(prev => [...prev, `Portfolio rebalanced successfully`]);
          setSuccess("Portfolio rebalanced successfully");
          
          // Refresh token data after rebalance
          await loadTokenData();
          setRebalanceData(null);
          
        } catch (error) {
          console.error("Error rebalancing portfolio:", error);
          setError(`Failed to rebalance portfolio: ${error.message}`);
          setTxStatus('Failed');
          
          // If rebalancePortfolio fails, fall back to manual rebalancing
          console.log("Falling back to manual rebalancing...");
          executeManualRebalance();
        } finally {
          setLoading(false);
          setExecutingStep('');
        }
      } else {
        // If rebalancePortfolio is not available, use manual rebalancing
        executeManualRebalance();
      }
    } else {
      // Show confirmation dialog before creating multiple proposals
      const totalProposalCount = rebalanceData.tokensToSell.length + rebalanceData.tokensToBuy.length;
      setShowMultiProposalConfirm(true);
    }
  };
  
  // FIXED: Added separate function for manual rebalancing (selling then buying)
  const executeManualRebalance = async () => {
    setLoading(true);
    setCompletedSteps([]);
    
    try {
      const { tokensToSell, tokensToBuy } = rebalanceData;
      
      // Step 1: Sell overweight tokens
      for (const tokenInfo of tokensToSell) {
        if (tokenInfo.amountToSell.gt(0)) {
          setExecutingStep(`Selling ${tokenInfo.formattedAmountToSell} ${tokenInfo.symbol}...`);
          
          try {
            const tx = await contract.sellToken(
              tokenInfo.address,
              tokenInfo.amountToSell,
              { gasLimit: 500000 }
            );
            
            setLastTxHash(tx.hash);
            setTxStatus('Mining');
            
            await tx.wait();
            setCompletedSteps(prev => [...prev, `Sold ${tokenInfo.formattedAmountToSell} ${tokenInfo.symbol}`]);
          } catch (error) {
            console.error(`Error selling ${tokenInfo.symbol}:`, error);
            setError(`Failed to sell ${tokenInfo.symbol}: ${error.message}`);
            break;
          }
        }
      }
      
      // Step 2: Buy underweight tokens
      for (const tokenInfo of tokensToBuy) {
        if (tokenInfo.ethToUse.gt(0)) {
          setExecutingStep(`Buying ${tokenInfo.formattedAmountToBuy} ${tokenInfo.symbol}...`);
          
          try {
            const tx = await contract.buyToken(
              tokenInfo.address,
              tokenInfo.ethToUse,
              { gasLimit: 500000 }
            );
            
            setLastTxHash(tx.hash);
            setTxStatus('Mining');
            
            await tx.wait();
            setCompletedSteps(prev => [...prev, `Bought ${tokenInfo.formattedAmountToBuy} ${tokenInfo.symbol}`]);
          } catch (error) {
            console.error(`Error buying ${tokenInfo.symbol}:`, error);
            setError(`Failed to buy ${tokenInfo.symbol}: ${error.message}`);
            break;
          }
        }
      }
      
      setSuccess("Portfolio rebalancing completed");
      // Refresh token data
      loadTokenData();
      // Clear rebalance data
      setRebalanceData(null);
      setTxStatus('Confirmed');
      
    } catch (error) {
      console.error("Error executing rebalance:", error);
      setError(`Rebalance execution failed: ${error.message}`);
      setTxStatus('Failed');
    } finally {
      setLoading(false);
      setExecutingStep('');
    }
  };

  // FIXED: Create a proposal to directly call rebalancePortfolio()
  const createRebalancePortfolioProposal = async () => {
    if (!governanceContract) {
      throw new Error("Governance contract not available");
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    
    try {
      // Get encoded call data for rebalancing portfolio
      const callData = createRebalancePortfolioCallData();
      
      // Create a governance proposal
      const description = "Rebalance Portfolio";
      const tx = await governanceContract.createProposal(
        10, // ProposalType.MiscAdminAction
        description,
        callData
      );
      
      setLastTxHash(tx.hash);
      console.log(`Proposal creation transaction sent: ${tx.hash}`);
      
      setTxStatus('Mining');
      await tx.wait();
      setTxStatus('Confirmed');
      
      setSuccess("Portfolio rebalance proposal created successfully. It will need to be voted on and executed.");
      
    } catch (error) {
      console.error("Error creating rebalance portfolio proposal:", error);
      setError(`Failed to create proposal: ${error.message}`);
      setTxStatus('Failed');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatPercent = (value) => {
    return (value / 100).toFixed(2) + '%';
  };

  // Render governance proposal modal
  const renderProposalModal = () => {
    if (!showProposalModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full">
          <h3 className="text-xl font-bold mb-4 text-blue-600">Create Governance Proposal</h3>
          <p className="mb-4">
            You don't have permission to directly execute rebalance operations. Would you like to create governance proposals instead?
          </p>
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <p className="font-medium">{proposalAction.description}</p>
            <p className="text-sm text-gray-600 mt-2">
              Note: Each buy and sell operation will require a separate governance proposal.
            </p>
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
              onClick={() => setShowProposalModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={() => {
                // FIXED: Give users an option for a single rebalancePortfolio proposal
                setShowProposalModal(false);
                createRebalancePortfolioProposal();
              }}
            >
              Create Single Rebalance Proposal
            </button>
            <button
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              onClick={() => {
                // Original option for multiple proposals
                setShowProposalModal(false);
                executeRebalance();
              }}
            >
              Create Multiple Proposals
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render multi-proposal confirmation modal
  const renderMultiProposalConfirm = () => {
    if (!showMultiProposalConfirm || !rebalanceData) return null;
    
    const totalProposalCount = rebalanceData.tokensToSell.length + rebalanceData.tokensToBuy.length;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full">
          <h3 className="text-xl font-bold mb-4 text-red-600">Multiple Proposals</h3>
          <p className="mb-4">
            This rebalance will create <strong>{totalProposalCount}</strong> separate governance proposals that will need to be voted on individually. 
          </p>
          <p className="mb-4">
            Are you sure you want to continue?
          </p>
          
          <div className="flex justify-end space-x-4">
            <button
              className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
              onClick={() => setShowMultiProposalConfirm(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={() => {
                setShowMultiProposalConfirm(false);
                createRebalancePortfolioProposal();
              }}
            >
              Create Single Proposal
            </button>
            <button
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              onClick={proceedWithMultipleProposals}
            >
              Create {totalProposalCount} Proposals
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render confirmation modal
  const renderConfirmModal = () => {
    if (!showConfirmModal || !rebalanceData) return null;
    
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <h2 className="text-xl font-bold mb-4">Confirm Rebalance</h2>
          
          <div className="mb-4">
            <p className="font-medium">This operation will execute the following trades:</p>
            
            {rebalanceData.tokensToSell.length > 0 && (
              <div className="mt-3">
                <h3 className="font-medium text-red-600">Tokens to sell:</h3>
                <ul className="mt-1 list-disc pl-5">
                  {rebalanceData.tokensToSell.map((token, i) => (
                    <li key={`sell-${i}`}>
                      Sell {token.formattedAmountToSell} {token.symbol} (worth ~{formatCurrency(token.excessValue)})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {rebalanceData.tokensToBuy.length > 0 && (
              <div className="mt-3">
                <h3 className="font-medium text-green-600">Tokens to buy:</h3>
                <ul className="mt-1 list-disc pl-5">
                  {rebalanceData.tokensToBuy.map((token, i) => (
                                          <li key={`buy-${i}`}>
                      Buy {parseFloat(token.formattedAmountToBuy) > 0 ? token.formattedAmountToBuy : (token.tokensToBuy ? token.tokensToBuy.toFixed(6) : '0')} {token.symbol} using ETH worth ~{formatCurrency(token.deficitValue)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="mt-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800">
              <p className="font-medium">Warning:</p>
              {hasPermission() ? (
                <p className="text-sm">This operation will execute multiple blockchain transactions and cannot be reversed. Gas fees will apply for each transaction.</p>
              ) : (
                <p className="text-sm">This will create multiple governance proposals (one for each trade). Each proposal will need to be voted on separately.</p>
              )}
            </div>
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={executeRebalance}
            >
              {hasPermission() ? "Rebalance Portfolio" : "Create Proposals"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render the debug modal
  const renderDebugModal = () => {
    if (!showDebugInfo || !debugData) return null;
    
    return (
      <div className="fixed inset-0 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4">
          <h2 className="text-xl font-bold mb-4 flex justify-between items-center">
            <span>Debug Information</span>
            <button 
              onClick={() => setShowDebugInfo(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
          </h2>
          
          <div className="mb-4">
            <h3 className="font-medium mb-2">Token Data</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Token</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Target %</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {debugData.rawTokenData.map((token, i) => (
                    <tr key={`token-${i}`} className={token.price <= 0 ? 'bg-red-50' : 'bg-white'}>
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">{token.symbol}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">{token.formattedBalance}</td>
                      <td className={`px-3 py-2 whitespace-nowrap text-sm ${token.price <= 0 ? 'text-red-600 font-bold' : ''}`}>
                        {token.price > 0 ? `${token.price.toFixed(2)}` : '0 (MISSING PRICE DATA)'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">${token.valueUSD.toFixed(2)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm">{(token.allocation / 100).toFixed(2)}%</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 text-xs">{token.address}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="mb-4">
            <h3 className="font-medium mb-2">Calculation Steps</h3>
            <div className="space-y-4">
              {debugData.calculationSteps.map((step, i) => (
                <div key={`step-${i}`} className="border rounded p-3">
                  <h4 className="font-medium">{step.step}</h4>
                  <pre className="bg-gray-50 p-2 mt-2 rounded text-xs overflow-x-auto">
                    {JSON.stringify(step, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              onClick={() => setShowDebugInfo(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <h2 className="text-xl font-semibold mb-4">Portfolio Rebalancing</h2>
      
      {/* Render the modals */}
      {renderProposalModal()}
      {renderConfirmModal()}
      {renderMultiProposalConfirm()}
      {renderDebugModal()}
      
      {/* Transaction Status */}
      {lastTxHash && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Transaction Status:</span>
            <span className={`text-sm font-bold ${
              txStatus === 'Confirmed' ? 'text-green-600' : 
              txStatus === 'Failed' ? 'text-red-600' : 
              'text-orange-600'
            }`}>
              {txStatus}
            </span>
          </div>
          <div className="text-xs mt-1">
            <a 
              href={`https://goerli.etherscan.io/tx/${lastTxHash}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              View on Etherscan: {lastTxHash.substring(0, 16)}...{lastTxHash.substring(lastTxHash.length - 16)}
            </a>
          </div>
        </div>
      )}
      
      {/* Token Allocations */}
      <div className="mb-6">
        <h3 className="font-medium mb-3">Current Portfolio Allocation</h3>
        
        {tokens.length === 0 ? (
          <div className="flex justify-center items-center h-40 bg-gray-50 rounded-lg">
            <p className="text-gray-500">
              {loading ? "Loading token data..." : "No tokens found in portfolio"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target %</th>
                  {rebalanceData && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current %</th>
                  )}
                  {rebalanceData && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tokens.map((token, index) => {
                  // Find matching token in rebalance data if available
                  const rebalanceToken = rebalanceData?.currentPortfolio.find(t => t.address === token.address);
                  
                  return (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">
                            {token.symbol}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {token.formattedBalance}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {token.price > 0 ? `$${token.price.toFixed(2)}` : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {token.valueUSD > 0 ? formatCurrency(token.valueUSD) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatPercent(token.allocation)}
                        </div>
                      </td>
                      {rebalanceData && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {rebalanceToken ? formatPercent(rebalanceToken.currentAllocation) : 'N/A'}
                          </div>
                        </td>
                      )}
                      {rebalanceData && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {rebalanceToken && (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                              ${rebalanceToken.status === 'overweight' ? 'bg-red-100 text-red-800' :
                                rebalanceToken.status === 'underweight' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'}`}>
                              {rebalanceToken.status}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        <div className="mt-4 flex justify-end">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            onClick={loadTokenData}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh Token Data"}
          </button>
        </div>
      </div>
      
      {/* Calculate Rebalance */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium mb-3">Calculate Rebalance</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slippage Tolerance (in basis points, 100 = 1%)
          </label>
          <input
            type="number"
            className="w-full p-2 border rounded-md"
            value={slippageTolerance}
            onChange={(e) => setSlippageTolerance(parseInt(e.target.value))}
            placeholder="100"
            min="1"
            max="500"
          />
        </div>
        
        <button
          className="w-full bg-green-600 text-white py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
          onClick={calculateRebalance}
          disabled={calculating || loading || tokens.length === 0}
        >
          {calculating ? "Calculating..." : "Calculate Rebalance"}
        </button>
      </div>
      
      {/* Rebalance Results */}
      {rebalanceData && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg">
          <h3 className="font-medium mb-3">Rebalance Analysis</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-sm text-gray-500">Total Portfolio Value</div>
              <div className="text-lg font-semibold">{formatCurrency(rebalanceData.summary.totalValueUSD)}</div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg text-center">
              <div className="text-sm text-red-500">Value to Sell</div>
              <div className="text-lg font-semibold text-red-600">{formatCurrency(rebalanceData.summary.totalToSell)}</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <div className="text-sm text-green-500">Value to Buy</div>
              <div className="text-lg font-semibold text-green-600">{formatCurrency(rebalanceData.summary.totalToBuy)}</div>
            </div>
          </div>
          
          {/* Tokens to Sell */}
          {rebalanceData.tokensToSell.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-red-600 mb-2">Tokens to Sell:</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rebalanceData.tokensToSell.map((token, i) => (
                      <tr key={`sell-${i}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{token.symbol}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{token.formattedAmountToSell}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{formatCurrency(token.excessValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Tokens to Buy */}
          {rebalanceData.tokensToBuy.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-green-600 mb-2">Tokens to Buy:</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount to Buy</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ETH Value to Use</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">USD Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rebalanceData.tokensToBuy.map((token, i) => (
                      <tr key={`buy-${i}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{token.symbol}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{token.formattedAmountToBuy}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{token.formattedEthToUse} ETH</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{formatCurrency(token.deficitValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* FIXED: Changed button text to reflect the action */}
          <button
            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 mt-4"
            onClick={() => setShowConfirmModal(true)}
            disabled={loading || (rebalanceData.tokensToSell.length === 0 && rebalanceData.tokensToBuy.length === 0)}
          >
            {loading ? "Processing..." : hasPermission() ? "Rebalance Portfolio" : "Create Rebalance Proposals"}
          </button>
        </div>
      )}
      
      {/* Execution Status */}
      {(executingStep || completedSteps.length > 0) && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium mb-3">Execution Status</h3>
          
          {/* Completed Steps */}
          {completedSteps.length > 0 && (
            <div className="mb-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Completed Steps:</h4>
              <ul className="list-disc pl-5 space-y-1">
                {completedSteps.map((step, index) => (
                  <li key={index} className="text-sm text-green-600">{step}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Current Step */}
          {executingStep && (
            <div className="flex items-center space-x-3 text-blue-600">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{executingStep}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Info Section */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              {hasPermission() ? (
                "This tool analyzes your current portfolio allocations and rebalances by executing the trades needed to align with your target allocations. It will sell overweight tokens and buy underweight tokens to restore balance."
              ) : (
                "This tool analyzes your current portfolio allocations and creates governance proposals for the trades needed to align with your target allocations. You can create either a single proposal or individual buy/sell proposals."
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RebalanceManager;